import { Suspense, lazy } from 'react';
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';
import Auth0ProviderWithHistory from '../Auth0ProviderWithNavigation';
import AuthenticatedRoute from './AuthenticatedRoute';
import Layout from '../components/Layout/Layout';
import SubPageLayout from '../components/Layout/SubPageLayout';
import PartnerLayout from '../components/Partner/PartnerLayout';
import { Widget } from '@typeform/embed-react';
import LoadingScreen from '../common/components/LoadingScreen';
import ErrorBoundary from '../components/ErrorBoundary';

const isChunkLoadError = (error: unknown): boolean =>
  error instanceof TypeError &&
  error.message.includes('Failed to fetch dynamically imported module');

// Preload critical components
const preloadComponent = <
  P extends Record<string, unknown> = Record<string, unknown>,
>(
  importFn: () => Promise<{ default: React.ComponentType<P> }>,
) => {
  const wrappedImport = () =>
    importFn().catch((error: unknown) => {
      if (isChunkLoadError(error)) {
        window.location.reload();
      }
      return Promise.reject(error);
    });

  const Component = lazy(wrappedImport);
  importFn().catch(() => {}); // Start loading immediately, ignore preload errors
  return Component;
};

// Lazy-loaded components with preload
const Home = preloadComponent(() => import('../pages/Home/Home'));
const Search = preloadComponent(() => import('../pages/Search/Search'));
const Boat = preloadComponent(() => import('../pages/Boat/Boat'));
const Payment = preloadComponent(() => import('../pages/Payment/Payment'));
const TripManagement = preloadComponent(
  () => import('../pages/TripManagement/TripManagement'),
);
const ThankYou = preloadComponent(() => import('../pages/ThankYou/ThankYou'));
const ClaimTrip = preloadComponent(() => import('../pages/Claim/ClaimTrip'));
const PrivacyPolicy = preloadComponent(
  () => import('../pages/PrivacyPolicy/PrivacyPolicy'),
);
const Profile = preloadComponent(() => import('../pages/Profile/Profile'));
const PartnerHome = preloadComponent(
  () => import('../pages/Partner/PartnerHome'),
);
const BoatsList = preloadComponent(
  () => import('../pages/Partner/Boats/BoatsList'),
);
const BoatDetail = preloadComponent(
  () => import('../pages/Partner/Boats/BoatDetail'),
);
const BookingsList = preloadComponent(
  () => import('../pages/Partner/Bookings/BookingsList'),
);
const BookingDetail = preloadComponent(
  () => import('../pages/Partner/Bookings/BookingDetail'),
);
const TripsList = preloadComponent(
  () => import('../pages/Partner/Trips/TripsList'),
);
const TripDetail = preloadComponent(
  () => import('../pages/Partner/Trips/TripDetail'),
);
const BasicPersonalInfo = preloadComponent(
  () => import('../pages/Profile/BasicPersonalInfo/BasicPersonalInfo'),
);
const DiveEquipment = preloadComponent(
  () => import('../pages/Profile/DiveEquipment/DiveEquipment'),
);
const DiveCertificate = preloadComponent(
  () => import('../pages/Profile/DiveCertificate/DiveCertificate'),
);
const TravelDocuments = preloadComponent(
  () => import('../pages/Profile/TravelDocuments/TravelDocuments'),
);
const Auth0Callback = preloadComponent(
  () => import('../components/auth0/auth0Callback'),
);
const Login = preloadComponent(() => import('../pages/Login'));
const ThailandGuide = preloadComponent(
  () => import('../pages/DestinationGuide/ThailandGuide'),
);
const GuestModalPreview = lazy(() => import('../pages/Dev/GuestModalPreview'));
const ErrorBoundaryPreview = lazy(
  () => import('../pages/Dev/ErrorBoundaryPreview'),
);

export const router = createBrowserRouter([
  {
    element: (
      <Suspense fallback={<LoadingScreen fullPage />}>
        <Auth0ProviderWithHistory />
      </Suspense>
    ),
    children: [
      {
        path: '/',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Home />
          </Suspense>
        ),
      },
      ...(import.meta.env.DEV
        ? [
            {
              path: '/dev/guest-modal-preview',
              element: (
                <Suspense fallback={<LoadingScreen fullPage />}>
                  <GuestModalPreview />
                </Suspense>
              ),
            },
            {
              path: '/dev/error-boundary-preview',
              element: (
                <Suspense fallback={<LoadingScreen fullPage />}>
                  <ErrorBoundaryPreview />
                </Suspense>
              ),
              errorElement: <ErrorBoundary />,
            },
          ]
        : []),
      {
        path: '/home',
        element: <Navigate to="/" replace />,
      },
      {
        path: '/callback',
        element: (
          <Suspense fallback={<LoadingScreen fullPage />}>
            <Auth0Callback />
          </Suspense>
        ),
      },
      {
        path: '/login',
        element: (
          <Suspense fallback={<LoadingScreen fullPage />}>
            <Login />
          </Suspense>
        ),
      },
      {
        path: '/boat/:boatSlug',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Boat />
          </Suspense>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: '/destinations/:countryName',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Search />
          </Suspense>
        ),
      },
      {
        path: '/routes/:mainRoute',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Search />
          </Suspense>
        ),
      },
      {
        path: '/search/mainroute/:mainRoute',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Search />
          </Suspense>
        ),
      },
      {
        path: '/search/country/:countryName',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Search />
          </Suspense>
        ),
      },
      {
        path: '/search/:token?',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Search />
          </Suspense>
        ),
      },
      {
        path: '/boat/:token?',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Boat />
          </Suspense>
        ),
        errorElement: <ErrorBoundary />,
      },
      {
        path: '/payment/:token?',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <Payment />
          </Suspense>
        ),
      },
      {
        path: '/thankyou',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <ThankYou />
          </Suspense>
        ),
      },
      {
        path: '/claim',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <ClaimTrip />
          </Suspense>
        ),
      },
      {
        path: '/user',
        element: (
          <>
            <Widget id="NKk2mvUP" style={{ width: '100%', height: '100vh' }} />
          </>
        ),
      },
      {
        path: '/app',
        element: <Layout />,
        children: [
          {
            element: (
              <AuthenticatedRoute>
                <Outlet />
              </AuthenticatedRoute>
            ),
            children: [
              {
                path: 'tripmanagement',
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <TripManagement />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      {
        path: '/profile',
        element: <Layout />,
        children: [
          {
            element: (
              <AuthenticatedRoute>
                <Outlet />
              </AuthenticatedRoute>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingScreen />}>
                    <Profile />
                  </Suspense>
                ),
              },
              {
                path: 'basic-personal-info',
                element: <SubPageLayout />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <BasicPersonalInfo />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'travel-documents',
                element: <SubPageLayout />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <TravelDocuments />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'dive-certificate',
                element: <SubPageLayout />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <DiveCertificate />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'dive-equipment',
                element: <SubPageLayout />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <DiveEquipment />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '/guides/thailand',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <ThailandGuide />
          </Suspense>
        ),
      },
      {
        path: '/privacypolicy',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
      {
        path: '/partner',
        element: (
          <AuthenticatedRoute>
            <PartnerLayout />
          </AuthenticatedRoute>
        ),
        errorElement: <ErrorBoundary />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PartnerHome />
              </Suspense>
            ),
          },
          {
            path: 'boats',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <BoatsList />
              </Suspense>
            ),
          },
          {
            path: 'boats/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <BoatDetail />
              </Suspense>
            ),
          },
          {
            path: 'bookings',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <BookingsList />
              </Suspense>
            ),
          },
          {
            path: 'bookings/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <BookingDetail />
              </Suspense>
            ),
          },
          {
            path: 'trips',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <TripsList />
              </Suspense>
            ),
          },
          {
            path: 'trips/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <TripDetail />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);
